import type { PropRuntimeGraph } from "../../game/assets/shared/types/PropRuntimeGraph";
import type { RuntimePropInstance } from "../../game/assets/pri_wqs/scripts/core/propMechanics/PropMechanicsTypes";
import { MagicEffectAnchor } from "./MagicGainEffects";
import { PropPreview } from "./PropPreview";
import { getRuntimeInstanceName, getSandboxTagLabel } from "./runtimeBridge";
import {
  CellContentRow,
  CellMeta,
  CellName,
  CellTagBadge,
  CellTextBlock,
  CellTimerBadge,
  CellValueBadge,
  CellValueRow,
} from "./styles";
import { PropImage } from "./visuals";

export interface PropCardProps {
  item?: RuntimePropInstance;
  catalog?: PropRuntimeGraph[];
  empty?: boolean;
  imageSize?: number;
  compact?: boolean;
  timerValue?: number;
  enableMagicEffectAnchor?: boolean;
}

export function PropCard({
  item,
  catalog,
  empty,
  imageSize = 30,
  timerValue,
  enableMagicEffectAnchor,
}: PropCardProps) {
  if (!item) {
    return (
      <CellContentRow>
        <PropImage empty size={imageSize} />
        <CellName $empty>{empty ? "空" : "未初始化"}</CellName>
      </CellContentRow>
    );
  }

  const content = (
    <>
      {!empty && Boolean(item.graph.tags?.length) && (
        <CellTagBadge title={formatTagBadgeTitle(item)}>{formatTagBadge(item)}</CellTagBadge>
      )}
      {!empty && typeof timerValue === "number" && (
        <CellTimerBadge title="计时器">⌛{timerValue}</CellTimerBadge>
      )}
      <CellContentRow>
        <PropImage graph={item.graph} empty={empty} size={imageSize} />
        <CellTextBlock>
          <CellName $empty={empty}>{getRuntimeInstanceName(item)}</CellName>
          {!empty && (
            <CellMeta>
              <MagicBadges item={item} />
            </CellMeta>
          )}
        </CellTextBlock>
      </CellContentRow>
    </>
  );

  return (
    <PropPreview item={item} catalog={catalog}>
      <MagicEffectAnchor itemId={item.id} disabled={!enableMagicEffectAnchor || empty}>
        {content}
      </MagicEffectAnchor>
    </PropPreview>
  );
}

function formatTagBadge(item: RuntimePropInstance): string {
  const tags = item.graph.tags ?? [];
  if (!tags.length) return "";
  const first = getSandboxTagLabel(tags[0]);
  return tags.length > 1 ? `${first} +${tags.length - 1}` : first;
}

function formatTagBadgeTitle(item: RuntimePropInstance): string {
  return (item.graph.tags ?? []).map(getSandboxTagLabel).join("、");
}

function MagicBadges({ item }: { item: RuntimePropInstance }) {
  const base = Number(item.graph.yield || 0);
  const grow = item.grow ?? 0;
  const collected = item.collectedMagic ?? 0;
  return (
    <CellValueRow>
      <CellValueBadge $tone="base">{base}</CellValueBadge>
      {grow !== 0 && <CellValueBadge $tone="grow">{formatSignedValue(grow)}</CellValueBadge>}
      {collected !== 0 && <CellValueBadge $tone="collect">{collected}</CellValueBadge>}
    </CellValueRow>
  );
}

function formatSignedValue(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}
